/**
 * UnifiProtectDashboardPage
 *
 * Multi-NVR dashboard. Each NVR is shown as a tab. The "Add NVR" button
 * opens the credentials form. Real-time Socket.IO events are routed to
 * the correct NVR by nvrId.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  useUnifiProtectStore,
  useNvrList,
  NvrConnectionState,
} from './unifi-protect-store';
import type { UnifiCamera, UnifiEvent } from './unifi-protect-store';
import { CredentialsSetupCard } from './components/CredentialsSetupCard';
import { CameraCard } from './components/CameraCard';
import { EventLog } from './components/EventLog';
import { NvrHealthPanel } from './components/NvrHealthPanel';

interface TerraMatrixSocket {
  on(
    event: 'unifi-protect:camera-status',
    handler: (payload: UnifiCamera & { nvrId: string }) => void,
  ): void;
  on(
    event: 'unifi-protect:event',
    handler: (payload: UnifiEvent & { nvrId: string }) => void,
  ): void;
  off(
    event: 'unifi-protect:camera-status',
    handler: (payload: UnifiCamera & { nvrId: string }) => void,
  ): void;
  off(
    event: 'unifi-protect:event',
    handler: (payload: UnifiEvent & { nvrId: string }) => void,
  ): void;
}

declare global {
  interface Window {
    __terramatrixSocket?: TerraMatrixSocket;
  }
}

interface UnifiProtectDashboardPageProps {
  embedded?: boolean;
}

export function UnifiProtectDashboardPage({ embedded = false }: UnifiProtectDashboardPageProps) {
  const nvrs = useNvrList();
  const {
    loadNvrs, loadCameras, removeNvr, connectNvr,
    handleCameraStatusEvent, handleProtectEvent,
  } = useUnifiProtectStore();

  const [selectedNvrId, setSelectedNvrId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const socketListenersAttached = useRef(false);

  // Load NVR list on mount
  useEffect(() => {
    loadNvrs();
  }, [loadNvrs]);

  // Load cameras for newly-connected NVRs
  useEffect(() => {
    for (const nvr of nvrs) {
      if (nvr.isConnected && nvr.cameras.length === 0 && !nvr.isLoading) {
        loadCameras(nvr.nvrId);
      }
    }
  }, [nvrs, loadCameras]);

  // Auto-select first connected NVR (or first NVR)
  useEffect(() => {
    if (selectedNvrId && nvrs.find(n => n.nvrId === selectedNvrId)) return;
    const connected = nvrs.find(n => n.isConnected);
    if (connected) { setSelectedNvrId(connected.nvrId); return; }
    if (nvrs.length > 0) setSelectedNvrId(nvrs[0].nvrId);
  }, [nvrs, selectedNvrId]);

  // Socket.IO real-time events
  useEffect(() => {
    if (socketListenersAttached.current) return;
    const io = window.__terramatrixSocket;
    if (!io) return;

    socketListenersAttached.current = true;
    io.on('unifi-protect:camera-status', handleCameraStatusEvent);
    io.on('unifi-protect:event', handleProtectEvent);

    return () => {
      io.off('unifi-protect:camera-status', handleCameraStatusEvent);
      io.off('unifi-protect:event', handleProtectEvent);
      socketListenersAttached.current = false;
    };
  }, [handleCameraStatusEvent, handleProtectEvent]);

  const selected: NvrConnectionState | undefined = selectedNvrId
    ? nvrs.find(n => n.nvrId === selectedNvrId)
    : undefined;

  // ── No NVRs yet ──────────────────────────────────────────────────────────

  if (nvrs.length === 0 && !showAddForm) {
    return (
      <div style={{
        minHeight: embedded ? 320 : '100%', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', padding: embedded ? '32px 0' : 40, gap: 32,
      }}>
        <div style={{ textAlign: 'center' }}>
          {embedded ? (
            <h2 style={{ fontSize: 22, fontWeight: 700, color: 'hsl(var(--foreground))', margin: 0 }}>
              UniFi Protect
            </h2>
          ) : (
            <h1 style={{ fontSize: 28, fontWeight: 700, color: 'hsl(var(--foreground))', margin: 0 }}>
              UniFi Protect
            </h1>
          )}
          <p style={{ color: 'hsl(var(--muted-foreground))', marginTop: 8, fontSize: 15 }}>
            Connect your UniFi Protect NVR to monitor cameras and receive real-time alerts.
          </p>
        </div>
        <CredentialsSetupCard onAdded={() => { setShowAddForm(false); loadNvrs(); }} />
      </div>
    );
  }

  // ── Main layout ──────────────────────────────────────────────────────────

  return (
    <div style={{ padding: embedded ? 0 : '24px 32px', minHeight: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        {embedded ? (
          <h2 style={{ fontSize: 22, fontWeight: 700, color: 'hsl(var(--foreground))', margin: 0 }}>
            UniFi Protect
          </h2>
        ) : (
          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'hsl(var(--foreground))', margin: 0 }}>
            UniFi Protect
          </h1>
        )}
        <button onClick={() => setShowAddForm(true)} style={primaryButtonStyle}>
          + Add NVR
        </button>
      </div>

      {/* Add NVR form */}
      {showAddForm && (
        <div style={{ marginBottom: 24 }}>
          <CredentialsSetupCard
            onAdded={() => { setShowAddForm(false); loadNvrs(); }}
            onCancel={() => setShowAddForm(false)}
          />
        </div>
      )}

      {/* NVR Tabs */}
      {nvrs.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          {nvrs.map(nvr => (
            <NvrTab
              key={nvr.nvrId}
              nvr={nvr}
              isSelected={nvr.nvrId === selectedNvrId}
              onClick={() => setSelectedNvrId(nvr.nvrId)}
            />
          ))}
        </div>
      )}

      {/* Selected NVR content */}
      {selected && !showAddForm && (
        <NvrPanel
          nvr={selected}
          onRefresh={() => loadCameras(selected.nvrId)}
          onConnect={() => connectNvr(selected.nvrId)}
          onRemove={async () => {
            if (confirm(`Remove "${selected.name}" and disconnect?`)) {
              await removeNvr(selected.nvrId);
              setSelectedNvrId(null);
            }
          }}
          onClearError={() => useUnifiProtectStore.getState().clearNvrError(selected.nvrId)}
        />
      )}
    </div>
  );
}

// ── NVR Tab ──────────────────────────────────────────────────────────────────

function NvrTab({
  nvr,
  isSelected,
  onClick,
}: {
  nvr: NvrConnectionState;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '7px 16px',
        borderRadius: 8,
        border: `1px solid ${isSelected ? 'rgba(34,197,94,0.4)' : 'hsl(var(--border))'}`,
        background: isSelected ? 'rgba(34,197,94,0.1)' : 'transparent',
        color: isSelected ? '#22c55e' : 'hsl(var(--muted-foreground))',
        fontSize: 13,
        fontWeight: isSelected ? 700 : 500,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        transition: 'all 0.15s ease',
      }}
    >
      <span style={{
        width: 7, height: 7, borderRadius: '50%',
        background: nvr.isConnected ? '#22c55e' : '#6b7280',
        flexShrink: 0,
      }} />
      {nvr.name}
      {nvr.isConnecting && <span style={{ fontSize: 10, opacity: 0.7 }}>…</span>}
    </button>
  );
}

// ── NVR Panel ─────────────────────────────────────────────────────────────────

function NvrPanel({
  nvr,
  onRefresh,
  onConnect,
  onRemove,
  onClearError,
}: {
  nvr: NvrConnectionState;
  onRefresh: () => void;
  onConnect: () => void;
  onRemove: () => void;
  onClearError: () => void;
}) {
  const cameras = nvr.cameras;

  return (
    <div>
      {/* NVR sub-header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 16, color: 'hsl(var(--foreground))' }}>{nvr.name}</div>
          {nvr.nvrInfo && (
            <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', marginTop: 2 }}>
              {nvr.nvrInfo.name} · {nvr.nvrInfo.version} · {nvr.nvrInfo.cameraCount} camera{nvr.nvrInfo.cameraCount !== 1 ? 's' : ''}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {nvr.isConnected ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '5px 12px',
              background: 'rgba(34,197,94,0.1)',
              border: '1px solid rgba(34,197,94,0.3)',
              borderRadius: 20, fontSize: 12, fontWeight: 600, color: '#22c55e',
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
              Connected
            </div>
          ) : (
            <button onClick={onConnect} disabled={nvr.isConnecting} style={ghostButtonStyle}>
              {nvr.isConnecting ? 'Connecting…' : 'Connect'}
            </button>
          )}

          <button onClick={onRefresh} disabled={nvr.isConnecting} style={ghostButtonStyle} title="Refresh cameras">
            ↺ Refresh
          </button>

          <button
            onClick={onRemove}
            style={{ ...ghostButtonStyle, color: '#f87171', borderColor: 'rgba(239,68,68,0.3)' }}
          >
            Remove
          </button>
        </div>
      </div>

      {/* Error banner */}
      {nvr.error && (
        <div style={{
          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 8, padding: '10px 16px', marginBottom: 20,
          color: '#f87171', fontSize: 14,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          {nvr.error}
          <button onClick={onClearError} style={{ background: 'none', border: 'none', color: '#f87171', cursor: 'pointer', fontSize: 16, padding: 0 }}>
            ✕
          </button>
        </div>
      )}

      {/* Camera grid + sidebar */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: cameras.length > 0 ? 'minmax(0, 1fr) 320px' : '1fr',
        gap: 24,
        alignItems: 'start',
      }}>
        <div>
          {nvr.isLoading ? (
            <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: 14, padding: 20 }}>Loading cameras…</div>
          ) : cameras.length === 0 ? (
            <div style={{
              background: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 12, padding: 40, textAlign: 'center',
              color: 'hsl(var(--muted-foreground))', fontSize: 14,
            }}>
              {nvr.isConnected
                ? 'No cameras found. Make sure cameras are adopted in UniFi Protect.'
                : 'NVR is not connected. Click Connect to establish a connection.'}
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
              {cameras.map(camera => (
                <CameraCard key={camera.id} camera={camera} />
              ))}
            </div>
          )}
        </div>

        {cameras.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <EventLog nvrId={nvr.nvrId} />
            <NvrHealthPanel nvrId={nvr.nvrId} />
          </div>
        )}
      </div>
    </div>
  );
}

const ghostButtonStyle: React.CSSProperties = {
  padding: '6px 14px',
  background: 'transparent',
  border: '1px solid hsl(var(--border))',
  borderRadius: 8,
  color: 'hsl(var(--muted-foreground))',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: 'rgba(34,197,94,0.15)',
  border: '1px solid rgba(34,197,94,0.4)',
  borderRadius: 8,
  color: '#22c55e',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};
